import type { AppearanceSettings } from 'ProdantixSdk/chat/appearance';
import type { ChatStrings } from 'ProdantixSdk/chat/panel';

/// One of the people the project shows (user picks of 2026-09-13): what the
/// served config's `team` carries, in the order the project picked them.
export interface TeamFace {
  name: string;
  initials: string;
  /// The colour the initials are drawn on when there is no picture: one of the
  /// record's two face tones, assigned by the edge from the face's place among
  /// those shown, and painted as given on the launcher, the header and a turn.
  tone: string;
  /// An edge path under `/v1/messenger/avatar`, which the client completes with
  /// its host and key, or an ID picture's own URL. Null draws the initials.
  avatarUrl: string | null;
}

/// A face typed by hand in the console before the team existed. Drawn only
/// while a project has picked no team.
export interface TypedFace {
  initials: string;
  tone: string;
}

export type LauncherFace = TypedFace & { avatarUrl?: string | null };

/// Two letters for a face with no picture, the way the edge derives them for
/// the team: the first letters of the first two words, or the first two
/// letters of a single word.
export function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter(word => word.length > 0);
  const letters = words.length > 1 ? `${words[0]?.slice(0, 1)}${words[1]?.slice(0, 1)}` : (words[0]?.slice(0, 2) ?? '');
  return letters.toUpperCase();
}

const GLYPH_PATH = 'M20 11.6a7.4 7.4 0 0 1-7.4 7.4H8.2L4 21.8l1-4.1A7.4 7.4 0 1 1 20 11.6Z';

const LAUNCHER_SIZE_PX: Record<AppearanceSettings['launcherSize'], number> = {
  large: 66,
  medium: 56,
  small: 46,
};

const LAUNCHER_SHAPE_CSS: Record<AppearanceSettings['launcherShape'], string> = {
  circle: 'width:var(--fab-size);height:var(--fab-size);border-radius:calc(var(--fab-size) / 2);',
  pill: 'width:auto;height:var(--fab-size);border-radius:calc(var(--fab-size) / 2);padding:0 22px;gap:10px;',
  squircle: 'width:var(--fab-size);height:var(--fab-size);border-radius:16px;',
};

const LAUNCHER_FILL_CSS: Record<AppearanceSettings['launcherFill'], string> = {
  filled: 'background:var(--a);color:#fff;',
  outlined: 'background:var(--paper);border:1px solid var(--hairline);color:var(--ink-2);box-shadow:0 1px 2px rgba(0,0,0,.08);',
};

const LAUNCHER_CONTENT_CSS: Record<AppearanceSettings['launcherContent'], string> = {
  avatar: '',
  glyph: '.launcher svg{width:calc(var(--fab-size) * 0.375);height:calc(var(--fab-size) * 0.375);}',
  team_faces: '.launcher .faces{display:flex;}.launcher .faces .av{box-shadow:0 0 0 2px var(--paper);}.launcher .faces .av+.av{margin-left:-10px;}',
};

/// Every launcher carries the dot, and the dot answers the project's Hours:
/// drawn while someone is there, hidden while nobody is. It belongs to no
/// content, which is why it left the content map (ruling 3 of 2026-09-13).
const LAUNCHER_LIVE_CSS = '.launcher .live{width:10px;height:10px;position:absolute;right:3px;bottom:3px;border-radius:50%;background:var(--online);box-shadow:0 0 0 2px var(--paper);}.launcher.closed .live{display:none}';

/// The unread count decomposes onto the same two axes as the launcher itself.
/// Position follows SHAPE: a pill has a row to sit the count in, a disc does not
/// and puts it at the corner. Colour follows FILL: an accent count is invisible
/// on an accent-filled launcher, which is why only the outlined forms take it,
/// and why the record calls the accent count the only colour an outlined
/// launcher ever gains.
const LAUNCHER_SHAPE_BADGE: Record<AppearanceSettings['launcherShape'], string> = {
  circle: '',
  pill: '.badge{position:static;box-shadow:none}',
  squircle: '',
};

const LAUNCHER_FILL_BADGE: Record<AppearanceSettings['launcherFill'], string> = {
  filled: '',
  outlined: '.badge{background:var(--a);top:-7px;right:-7px;min-width:19px;height:19px;font-size:11.5px}',
};

const LAUNCHER_SHAPE_LABEL: Record<AppearanceSettings['launcherShape'], string> = {
  circle: '',
  pill: '.launcher .label{font-size:14px;font-weight:600;white-space:nowrap;}',
  squircle: '',
};

const LAUNCHER_SHAPE_RESPONSIVE: Record<AppearanceSettings['launcherShape'], string> = {
  circle: '',
  pill: '@media (max-width:480px){.launcher{width:var(--fab-size);padding:0}.launcher .label{display:none}}',
  squircle: '',
};

export function launcherCss(shape: AppearanceSettings['launcherShape'], fill: AppearanceSettings['launcherFill'], content: AppearanceSettings['launcherContent'], size: AppearanceSettings['launcherSize']): string {
  return `.launcher{--fab-size:${LAUNCHER_SIZE_PX[size]}px;${LAUNCHER_SHAPE_CSS[shape]}${LAUNCHER_FILL_CSS[fill]}}${LAUNCHER_CONTENT_CSS[content]}${LAUNCHER_LIVE_CSS}${LAUNCHER_SHAPE_BADGE[shape]}${LAUNCHER_FILL_BADGE[fill]}${LAUNCHER_SHAPE_LABEL[shape]}${LAUNCHER_SHAPE_RESPONSIVE[shape]}`;
}

export function buildLauncherContent(content: AppearanceSettings['launcherContent'], strings: ChatStrings, team: LauncherFace[], resolveAvatar: (url: string) => string = url => url): DocumentFragment {
  const fragment = document.createDocumentFragment();
  /* A faces launcher with nobody on the team draws the glyph (user pick,
     2026-09-14), so it never shows a face belonging to no one and returns to
     faces the moment the project picks somebody. It used to draw one disc
     carrying the assistant's initials, which was the fallback for the typed
     faces that no longer exist. */
  if (content === 'glyph' || team.length === 0) {
    const wrapper = document.createElement('span');
    wrapper.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${GLYPH_PATH}"/></svg>`;
    fragment.append(...wrapper.childNodes, buildLiveDot());
    return fragment;
  }
  if (content === 'avatar') {
    fragment.append(buildFace(team[0], strings, resolveAvatar), buildLiveDot());
    return fragment;
  }
  fragment.append(buildTeamFaces(team, strings, resolveAvatar), buildLiveDot());
  return fragment;
}

/// The team's faces as one row, for the launcher and for the panel's header.
///
/// Exported because the header draws the same faces the launcher does, and the
/// console's preview draws them a third time. Two of those three at least come
/// from here.
export function buildTeamFaces(team: LauncherFace[], strings: ChatStrings, resolveAvatar: (url: string) => string = url => url): HTMLSpanElement {
  const faces = document.createElement('span');
  faces.className = 'faces';
  for (const face of team.slice(0, 2)) faces.append(buildFace(face, strings, resolveAvatar));
  return faces;
}

/// The tone is painted as the edge sent it, on every surface alike. The header
/// used to be the exception: the edge sent the project's accent as every tone,
/// the standard skin paints the header in that accent, and a disc the colour of
/// the header behind it is invisible, so the header left the fill to a rule
/// that darkened the accent, and the preview and the widget drew two different
/// faces for one person (user report, 2026-09-20). The edge now sends the
/// record's own two tones, so one drawing serves everywhere.
function buildFace(face: LauncherFace | undefined, strings: ChatStrings, resolveAvatar: (url: string) => string): HTMLSpanElement {
  const avatar = document.createElement('span');
  avatar.className = 'av';
  avatar.setAttribute('aria-hidden', 'true');
  if (face) avatar.style.background = face.tone;
  if (face?.avatarUrl) {
    const picture = document.createElement('img');
    picture.alt = '';
    picture.src = resolveAvatar(face.avatarUrl);
    avatar.append(picture);
    return avatar;
  }
  avatar.textContent = face ? face.initials : strings.agentLabel.slice(0, 2).toUpperCase();
  return avatar;
}

function buildLiveDot(): HTMLSpanElement {
  const live = document.createElement('span');
  live.className = 'live';
  live.setAttribute('aria-hidden', 'true');
  return live;
}
